<template>
  <div class="the-login">
    <h1
      class="text-left text-2xl font-bold text-gray-900 sm:text-3xl"
      data-testid="login-title"
    >
      {{ t("core.modules.account.login.h1") }}
    </h1>
    <DsFlash />
    <DsForm
      v-slot="{ meta }"
      autocomplete="off"
      :validation-schema="validationSchema"
      data-testid="login-form"
      @submit="onSubmit"
    >
      <DsInput
        id="email"
        name="email"
        type="email"
        :label="t('common.form.input.email.label')"
        :placeholder="t('common.form.input.email.placeholder')"
        autocomplete="email"
        maxlength="255"
        data-testid="login-email"
      />
      <DsInput
        id="password"
        name="password"
        type="password"
        :label="t('common.form.input.password.label')"
        :placeholder="t('common.form.input.password.placeholder')"
        maxlength="32"
        data-testid="login-password"
      />
      <DsFormButton
        :loading="loading"
        :disabled="!meta.valid"
        class="mt-4 w-full lg:mt-6"
        size="lg"
      >
        {{ t("common.button.login") }}
      </DsFormButton>
    </DsForm>
    <div class="flex flex-col space-y-4 lg:space-y-6">
      <p class="text-sm text-gray-900 md:text-base">
        {{ t("core.modules.account.login.text.account") }}
        <DsLink :to="{ name: 'Register' }" data-testid="login-signup">
          {{ t("core.modules.account.login.link.signUp") }}
        </DsLink>
      </p>
      <p class="text-sm md:text-base">
        <DsLink :to="{ name: 'ForgotPassword' }" data-testid="forgot-password">
          {{ t("core.modules.account.login.link.forgot") }}
        </DsLink>
      </p>
    </div>
    <div class="flex flex-col space-y-4 lg:space-y-6">
      <DsSocialButton
        name="google"
        :text="t('common.button.socialMedia.signIn.google')"
        :alt="t('common.button.socialMedia.alt.google')"
        :loading="googleLoading"
        data-testid="social-google"
        class="w-full"
        outline
        @click="onClickGoogle"
      />
      <DsSocialButton
        name="apple"
        :text="t('common.button.socialMedia.signIn.apple')"
        :alt="t('common.button.socialMedia.alt.apple')"
        :loading="appleLoading"
        class="w-full"
        data-testid="social-apple"
        outline
        @click="onClickApple"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";

import {
  DsFlash,
  DsFlashNotifier,
  DsForm,
  DsFormButton,
  DsInput,
  DsLink,
  DsSocialButton,
} from "@devsalsa/vue-core";

import { MarketingTracking } from "@/core/modules/account/helpers/MarketingTracking";
import AccountService from "@/core/modules/account/services/AccountService";
import type { LoginFormData } from "@/core/modules/account/services/AccountService.types";
import AccountHandler from "@/core/shared/helpers/Account/AccountHandler";
import { ErrorTranslator } from "@/core/shared/helpers/Error/ErrorTranslator";
import { FormHandler } from "@/core/shared/helpers/FormHandler";
import SocialAuthService, {
  AccountExistsFirebaseError,
} from "@/core/shared/services/Auth/SocialAuthService";
import { BadRequestApiServiceError } from "@/core/shared/services/Error/ApiServiceError";

import { FirebaseError } from "@firebase/util";
import type { OAuthCredential } from "firebase/auth";
import type { SubmissionContext } from "vee-validate";
import { useI18n } from "vue-i18n";
import { type NavigationFailure, useRoute, useRouter } from "vue-router";
import { object, string } from "yup";

defineOptions({
  name: "TheLogin",
});

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const loading = ref<boolean>(false);
const googleLoading = ref<boolean>(false);
const appleLoading = ref<boolean>(false);
const validationSchema = object({
  email: string()
    .trim()
    .required(t("common.form.input.email.validation.required"))
    .email(t("common.form.input.email.validation.invalid"))
    .max(255, t("common.error.validation.maxStringChars", { max: 255 })),
  password: string()
    .trim()
    .required(t("common.form.input.password.validation.required"))
    .min(10, t("common.form.input.password.validation.min"))
    .max(32, t("common.error.validation.maxStringChars", { max: 32 })),
});
let credential: OAuthCredential | undefined = undefined;
const code = (route.redirectedFrom?.params.code as string) ?? "";

onMounted(() => {
  if (code) {
    DsFlashNotifier.info(t("core.modules.account.login.verifyCode.message"));
  }
});

async function toDashboard(
  token: string
): Promise<void | NavigationFailure | undefined> {
  await AccountHandler.authenticate(token);
  if (code) {
    return await router.push({
      name: "VerifyEmailCode",
      params: { code: code },
    });
  }
  return router.push({
    name: "Dashboard",
  });
}

async function onSubmit(
  data: LoginFormData,
  actions: SubmissionContext
): Promise<void | NavigationFailure | undefined> {
  loading.value = true;
  DsFlashNotifier.hide();
  try {
    const tokenResponse = await AccountService.login(data.email, data.password);
    return await toDashboard(tokenResponse.token);
  } catch (error) {
    if (!(error instanceof BadRequestApiServiceError)) {
      throw error;
    }

    DsFlashNotifier.error(ErrorTranslator.translate(error));
    FormHandler.setErrors(error, actions);
    loading.value = false;
  }
}

async function onClickGoogle(): Promise<void | NavigationFailure | undefined> {
  googleLoading.value = true;
  try {
    const formData = new FormData();
    const socialTokenResponse = await SocialAuthService.google(credential);
    formData.append("id_token", socialTokenResponse.token);
    MarketingTracking.appendUtmParameters(formData);
    MarketingTracking.appendReferrerId(formData);
    MarketingTracking.appendReferrerUrl(formData);
    MarketingTracking.appendFacebookClickId(formData);
    MarketingTracking.appendGoogleClientId(formData);

    const tokenResponse = await AccountService.loginWithGoogle(formData);
    return await toDashboard(tokenResponse.token);
  } catch (error) {
    if (
      !(
        error instanceof BadRequestApiServiceError ||
        error instanceof FirebaseError
      )
    ) {
      throw error;
    }

    if (error instanceof AccountExistsFirebaseError) {
      credential = error.customData?.credential as OAuthCredential;
      DsFlashNotifier.info(ErrorTranslator.translate(error));
    } else {
      DsFlashNotifier.error(ErrorTranslator.translate(error));
    }
  } finally {
    googleLoading.value = false;
  }
}

async function onClickApple(): Promise<void | NavigationFailure | undefined> {
  appleLoading.value = true;
  try {
    const formData = new FormData();
    const socialTokenResponse = await SocialAuthService.apple(credential);
    formData.append("id_token", socialTokenResponse.token);
    MarketingTracking.appendUtmParameters(formData);
    MarketingTracking.appendReferrerId(formData);
    MarketingTracking.appendReferrerUrl(formData);
    MarketingTracking.appendFacebookClickId(formData);
    MarketingTracking.appendGoogleClientId(formData);

    const tokenResponse = await AccountService.loginWithApple(formData);
    return await toDashboard(tokenResponse.token);
  } catch (error) {
    if (
      !(
        error instanceof BadRequestApiServiceError ||
        error instanceof FirebaseError
      )
    ) {
      throw error;
    }

    if (error instanceof AccountExistsFirebaseError) {
      credential = error.customData?.credential as OAuthCredential;
      DsFlashNotifier.info(ErrorTranslator.translate(error));
    } else {
      DsFlashNotifier.error(ErrorTranslator.translate(error));
    }
  } finally {
    appleLoading.value = false;
  }
}
</script>

<style lang="scss" scoped>
.the-login {
  @apply flex flex-col space-y-4 lg:space-y-6;
}
</style>
